import * as React from 'react';
import { useContext, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { DealState, PlayerState } from '../types/playlist';
import axios from 'axios';
import { OrganisationContext } from '../../App';
import { getApiBaseUrl } from '../../utils/env';
import { useParams } from 'react-router-dom';
import { SSP } from '../types/ssp';
import { useSelector } from 'react-redux';
import { RootState } from '../../index';
import { PlaylistState } from '../../store/playlistSlice';

const CloseBtnRoot = styled('div')`
    position: absolute;
    right: 10px;
    top: 10px;
`;

const Root = styled('div')`
    padding: 20px 40px 0;
    position: relative;
`;

const TitleDialog = styled(DialogTitle)`
    position: relative;
    padding-top: 0;

    &:after {
        content: ' ';
        background-color: #ccc;
        width: calc(100% + 80px);
        height: 1px;
        position: absolute;
        left: -40px;
        bottom: 10px;
    }
`;

const Title = styled('div')`
    font-weight: bold;
    margin: 24px 0 0 0;
    color: black;
`;

type AddCreativeDealDialogProps = {
  open: boolean;
  handleClose: () => void;
  deal: DealState;
  // Kept for parity with other dialogs; not required here but available if needed by callers
  creative?: PlayerState;
  // Optional SSP options override
};

export const AddCreativeDealDialog = ({
  open,
  handleClose,
  deal,
}: AddCreativeDealDialogProps) => {
  const { tagId } = useParams()
  const { selectedOrganisation } = useContext(OrganisationContext);

    const {
    geoCountries
  } = useSelector<RootState, PlaylistState>((state) => state.playlist);

  const [ssp, setSsp] = useState<string>(deal.ssp || '');
  const [dealId, setDealId] = useState<string>(deal.deal_id || '');
  const [segment, setSegment] = useState<string>((Array.isArray(deal.segments) && deal.segments.join(', ')) || '');
  const [countries, setCountries] = useState<string[]>(deal.geos || []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: DealState = {
      ...deal,
      ssp,
      deal_id: dealId,
      segments: segment ? segment.split(',').map((s) => s.trim()) : [],
      geos: countries,
    };

    try {
      if (!payload.id) {
        await axios.post(`${getApiBaseUrl()}/playlist-tag/${selectedOrganisation}/deal/${tagId}`, payload);
      } else {
        await axios.patch(`${getApiBaseUrl()}/playlist-tag/${selectedOrganisation}/deal/${tagId}/${payload.id}`, payload);
      }
    } finally {
      handleClose();
    }
  };

  const isEdit = Boolean(deal?.id);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth={'sm'}>
      <Root>
        <CloseBtnRoot>
          <IconButton onClick={handleClose}>
            <CloseIcon/>
          </IconButton>
        </CloseBtnRoot>
        <TitleDialog>
          <Title>{`${isEdit ? 'Edit' : 'Add'} deal`}</Title>
        </TitleDialog>
        <DialogContent>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2} style={{ marginTop: 0 }}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mt: 0 }}>
                  <InputLabel id="sspLabel" required>SSP</InputLabel>
                  <Select
                    id="ssp"
                    labelId="sspLabel"
                    label="SSP"
                    required
                    value={ssp}
                    onChange={(e) => setSsp(e.target.value as string)}
                    displayEmpty
                    variant="outlined"
                  >
                    { Object.entries(SSP).map(([key, value]) => (
                      <MenuItem key={key} value={key}>{value}</MenuItem>
                    )) }
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <TextField
                    id="dealId"
                    required
                    label="Deal ID"
                    variant="outlined"
                    type="text"
                    value={dealId}
                    onChange={({ target: { value }}) => setDealId(value)}
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="countries-label">Geos</InputLabel>
                  <Select
                    labelId="countries-label"
                    id="countries"
                    multiple
                    label={'Geos'}
                    value={countries}
                    onChange={(e) => setCountries(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                  >
                    {geoCountries.map(({ country, alpha_2_code}) => (
                      <MenuItem key={alpha_2_code} value={alpha_2_code}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <TextField
                    id="segment"
                    label="Segment"
                    variant="outlined"
                    type="text"
                    value={segment}
                    onChange={({ target: { value }}) => setSegment(value)}
                    placeholder="e.g., sports_fans_uk"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Grid container justifyContent={'flex-end'}>
                  <Button type={'submit'} color="primary" variant="contained">
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Root>
    </Dialog>
  );
};
